export interface AudioAnalyzerConfig {
  fftSize?: number;
  smoothingTimeConstant?: number;
  minDecibels?: number;
  maxDecibels?: number;
}

export interface FrequencyData {
  frequencies: Uint8Array;
  timeDomain: Uint8Array;
  average: number;
  peak: number;
}

export class AudioContextManager {
  private static instance: AudioContextManager;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): AudioContextManager {
    if (!AudioContextManager.instance) {
      AudioContextManager.instance = new AudioContextManager();
    }
    return AudioContextManager.instance;
  }

  async initialize(config: AudioAnalyzerConfig = {}): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      const AudioContextConstructor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;

      this.audioContext = new AudioContextConstructor();

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = config.fftSize || 256;
      this.analyser.smoothingTimeConstant = config.smoothingTimeConstant || 0.8;
      this.analyser.minDecibels = config.minDecibels || -90;
      this.analyser.maxDecibels = config.maxDecibels || -10;

      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize audio context:", error);
      throw error;
    }
  }

  connectAudioElement(audioElement: HTMLAudioElement): void {
    if (!this.audioContext || !this.analyser) {
      throw new Error("Audio context not initialized");
    }

    if (this.audioElement === audioElement && this.source) {
      return;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    this.audioElement = audioElement;

    try {
      this.source = this.audioContext.createMediaElementSource(audioElement);
      this.source.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);
    } catch (error) {
      console.warn(
        "Failed to create media element source, element may already be connected:",
        error
      );
      this.audioElement = null;
      throw error;
    }
  }

  getFrequencyData(): FrequencyData | null {
    if (!this.analyser) {
      return null;
    }

    const bufferLength = this.analyser.frequencyBinCount;
    const frequencies = new Uint8Array(bufferLength);
    const timeDomain = new Uint8Array(bufferLength);

    this.analyser.getByteFrequencyData(frequencies);
    this.analyser.getByteTimeDomainData(timeDomain);

    let sum = 0;
    let peak = 0;
    for (let i = 0; i < frequencies.length; i++) {
      sum += frequencies[i];
      peak = Math.max(peak, frequencies[i]);
    }
    const average = sum / frequencies.length;

    return {
      average,
      frequencies,
      peak,
      timeDomain,
    };
  }

  getAudioLevel(): number {
    const data = this.getFrequencyData();
    if (!data) {
      return 0;
    }
    return data.average / 255;
  }

  isRunning(): boolean {
    return this.audioContext?.state === "running";
  }

  async resume(): Promise<void> {
    if (this.audioContext?.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  dispose(): void {
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close();
    }

    this.audioContext = null;
    this.analyser = null;
    this.audioElement = null;
    this.isInitialized = false;
  }
}

export async function generateWaveform(
  audioUrl: string,
  canvasWidth: number = 400,
  canvasHeight: number = 100
): Promise<number[]> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    audio.crossOrigin = "anonymous";
    audio.src = audioUrl;

    audio.addEventListener("loadeddata", () => {
      try {
        const audioContext = new (
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext
        )();
        const source = audioContext.createMediaElementSource(audio);
        const analyser = audioContext.createAnalyser();

        analyser.fftSize = 2048;
        source.connect(analyser);
        analyser.connect(audioContext.destination);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        analyser.getByteFrequencyData(dataArray);

        const waveform: number[] = [];
        const step = Math.floor(dataArray.length / canvasWidth);

        for (let i = 0; i < canvasWidth; i++) {
          const index = i * step;
          const value = dataArray[index] / 255;
          waveform.push(value);
        }

        audioContext.close();
        resolve(waveform);
      } catch (error) {
        reject(error);
      }
    });

    audio.addEventListener("error", () => {
      reject(new Error("Failed to load audio file"));
    });

    audio.load();
  });
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function lerp(start: number, end: number, factor: number): number {
  return start + (end - start) * factor;
}

export function smoothStep(t: number): number {
  return t * t * (3 - 2 * t);
}
